import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { ResponsiveBar } from "@nivo/bar";
import { Table } from "semantic-ui-react";
import {
  CHART_THEME,
  LEGEND_CONFIG,
  renderTruncatedText,
  createFocusableBarLayer,
  getChartContainerProps,
  getAccessibleTextColor
} from "./chartUtils";

const StackedBarByAudit = ({
  barGraphDataByAudit,
  statusKeys,
  handleChartClick,
  graphTextColor,
  chartType,
  showDataTables,
  styles
}) => {
  const totalRecommendations = _.sumBy(barGraphDataByAudit, "totalCount");

  const ariaLabel =
    chartType === "investigation"
      ? `Interactive bar chart showing ${totalRecommendations} recommendations by investigation. Tab through individual bars to filter data. Press enter to populate the Recommendation List with corresponding bar graph data segment.`
      : `Interactive bar chart showing ${totalRecommendations} recommendations by audit. Tab through individual bars to filter data. Press enter to populate the Recommendation List with corresponding bar graph data segment.`;

  const tableLabel =
    chartType === "investigation"
      ? `Detailed Breakdown of ${totalRecommendations} Recommendations by
            Investigation and Status (Top 20)`
      : `Detailed Breakdown of ${totalRecommendations} Recommendations by
            Audit and Status (Top 20)`;

  return (
    <>
      <div {...getChartContainerProps()} aria-label={ariaLabel}>
        <ResponsiveBar
          data={barGraphDataByAudit}
          labelTextColor={({ color }) =>
            color ? getAccessibleTextColor(color) : graphTextColor || "#000000"
          }
          keys={statusKeys}
          layout="horizontal"
          indexBy="auditName"
          margin={{ top: 10, right: 170, bottom: 50, left: 300 }}
          padding={0.2}
          colors={({ id, data }) => data.colors[id]}
          borderWidth={1.5}
          theme={CHART_THEME}
          legends={[LEGEND_CONFIG]}
          animate
          motionStiffness={90}
          motionDamping={15}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "# Recommendations",
            legendPosition: "middle",
            legendOffset: 40
          }}
          enableGridY={false}
          axisLeft={{
            renderTick: d => renderTruncatedText(d, 40)
          }}
          onClick={e => handleChartClick(e, "status", "auditName")}
          layers={[
            "grid",
            "axes",
            "bars",
            "markers",
            "legends",
            "annotations",
            createFocusableBarLayer(
              statusKeys,
              handleChartClick,
              "status",
              "auditName",
              { sortBy: ["data.indexValue"], order: ["desc"] }
            )
          ]}
        />
      </div>
      <div css={styles} className={showDataTables ? "" : "sr-only"}>
        <Table
          id="audit-chart-data-table"
          aria-label="Recommendations data by audit and status"
          basic="very"
          celled
          compact
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                colSpan={
                  statusKeys.filter(status =>
                    barGraphDataByAudit.some(audit => audit[status] > 0)
                  ).length + 2
                }
                textAlign="center"
                style={{ fontWeight: "bold" }}
                scope="col"
              >
                {tableLabel}
              </Table.HeaderCell>
            </Table.Row>

            <Table.Row>
              <Table.HeaderCell scope="col">Audit</Table.HeaderCell>
              {statusKeys
                .filter(status =>
                  barGraphDataByAudit.some(audit => audit[status] > 0)
                )
                .map(status => (
                  <Table.HeaderCell key={status} scope="col" textAlign="center">
                    {status}
                  </Table.HeaderCell>
                ))}
              <Table.HeaderCell scope="col" textAlign="center">
                Total
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {barGraphDataByAudit.map(auditData => {
              const relevantStatuses = statusKeys.filter(status =>
                barGraphDataByAudit.some(audit => audit[status] > 0)
              );
              const auditTotal = relevantStatuses.reduce(
                (sum, status) => sum + (auditData[status] || 0),
                0
              );
              return (
                <Table.Row key={auditData.auditId}>
                  <Table.HeaderCell scope="row" textAlign="left">
                    {auditData.auditName}
                  </Table.HeaderCell>
                  {relevantStatuses.map(status => (
                    <Table.Cell key={status} textAlign="center">
                      {auditData[status] || 0}
                    </Table.Cell>
                  ))}
                  <Table.Cell textAlign="center">{auditTotal}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </>
  );
};

StackedBarByAudit.propTypes = {
  barGraphDataByAudit: PropTypes.arrayOf(
    PropTypes.shape({
      auditName: PropTypes.string.isRequired
    })
  ).isRequired,
  statusKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleChartClick: PropTypes.func.isRequired,
  graphTextColor: PropTypes.string,
  chartType: PropTypes.string.isRequired,
  showDataTables: PropTypes.bool.isRequired,
  styles: PropTypes.shape({})
};

StackedBarByAudit.defaultProps = {
  graphTextColor: null,
  styles: {}
};

export default StackedBarByAudit;
